import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { navItems } from '../../routes';
import { usePermission } from '../../auth/usePermission';
import { useStepApprovalCount } from '../../hooks/useStepApprovalCount';
import { useStepEditCount } from '../../hooks/useStepEditCount';
import { useStepSubmissionCount } from '../../hooks/useStepSubmissionCount';
import { useAssignedJobsCount } from '../../hooks/useAssignedJobsCount';
import { useBulkRequestCount } from '../../hooks/useBulkRequestCount';
import { useRejectedRequestCount } from '../../hooks/useRejectedRequestCount';
import { useWorkflowNotificationCount } from '../../hooks/useWorkflowNotificationCount';
import { useCandidateRequestCount } from '../../hooks/useCandidateRequestCount';
import { NAV_ICONS, IconPanelToggle, IconChevronDown, IconRefresh } from '../ui/icons';

// Maps a nav entry's `badge` key (routes.js) to the live count to show as a pill after its
// label. Add an entry here whenever a new nav item needs a count badge.
function useBadgeCounts() {
  return {
    'step-approvals': useStepApprovalCount(),
    'step-submissions': useStepSubmissionCount(),
    'step-edits': useStepEditCount(),
    'bulk-requests': useBulkRequestCount(),
    'passport-requests': useCandidateRequestCount('passport'),
    'accommodation-requests': useCandidateRequestCount('accommodation'),
    'rejected-requests': useRejectedRequestCount(),
    'assigned-jobs': useAssignedJobsCount(),
    'workflow-notifications': useWorkflowNotificationCount(),
  };
}

function CountPill({ count }) {
  if (!count) return null;
  return (
    <span className="ml-auto inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-white">
      {count > 99 ? '99+' : count}
    </span>
  );
}

// A nav path that is a prefix of another nav path (/candidates vs /candidates/pending-edits)
// must match exactly, otherwise both highlight on the longer one. Detail pages like
// /candidates/9 still fall through to the shorter one.
const ALL_NAV_PATHS = navItems.flatMap((i) => (i.children ? i.children.map((c) => c.path) : [i.path]));
const hasLongerNavPath = (path) => ALL_NAV_PATHS.some((p) => p !== path && p.startsWith(`${path}/`));

// The nav path that owns the current URL: the longest one equal to it or a parent of it. Matching on
// a bare prefix would light up /candidates (and its group) on every /candidates/... page of other groups.
const activeNavPath = (pathname) =>
  ALL_NAV_PATHS.filter((p) => pathname === p || pathname.startsWith(`${p}/`)).sort((a, b) => b.length - a.length)[0];

function NavRow({ path, label, badgeCount, isActive, collapsed, nested = false }) {
  const Icon = NAV_ICONS[path] || NAV_ICONS.default;
  return (
    <NavLink
      to={path}
      end={path === '/' || hasLongerNavPath(path)}
      title={collapsed ? label : undefined}
      className={({ isActive: linkActive }) =>
        `group relative flex items-center gap-3 rounded-lg py-2.5 text-sm font-medium transition-colors ${
          collapsed ? 'justify-center px-0' : nested ? 'pl-10 pr-3' : 'px-3'
        } ${(isActive ?? linkActive) ? 'bg-white/[0.06] text-white' : 'text-gray-400 hover:bg-white/[0.04] hover:text-gray-100'}`
      }
    >
      {({ isActive: linkActive }) => {
        const active = isActive ?? linkActive;
        return (
          <>
            {!nested && (
              <span
                className={`absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-primary transition-opacity ${
                  active ? 'opacity-100 shadow-[0_0_10px_1px_rgba(220,38,38,0.65)]' : 'opacity-0'
                }`}
              />
            )}
            {!nested && (
              <Icon
                className={`h-[18px] w-[18px] shrink-0 ${
                  active ? 'text-primary' : 'text-gray-500 group-hover:text-gray-300'
                }`}
              />
            )}
            {!collapsed && <span className="truncate">{label}</span>}
            {!collapsed && <CountPill count={badgeCount} />}
          </>
        );
      }}
    </NavLink>
  );
}

function NavGroup({ group, collapsed, badgeCounts, isChildActive, open, onToggle }) {
  const GroupIcon = NAV_ICONS[`/${group.label.toLowerCase()}`] || NAV_ICONS.default;
  const groupBadgeTotal = group.children.reduce((sum, c) => sum + (badgeCounts[c.badge] || 0), 0);

  // No room to show nested items when the sidebar itself is icon-only — link straight to the
  // first visible child instead of trying to render a flyout.
  if (collapsed) {
    return <NavRow path={group.children[0].path} label={group.label} isActive={isChildActive} collapsed />;
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className={`group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
          isChildActive ? 'text-white' : 'text-gray-400 hover:bg-white/[0.04] hover:text-gray-100'
        }`}
      >
        <GroupIcon className={`h-[18px] w-[18px] shrink-0 ${isChildActive ? 'text-primary' : 'text-gray-500 group-hover:text-gray-300'}`} />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <CountPill count={groupBadgeTotal} />
        <IconChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {group.children.map((child) => (
            <NavRow key={child.path} path={child.path} label={child.label} badgeCount={badgeCounts[child.badge]} collapsed={false} nested />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar() {
  const { can } = usePermission();
  const location = useLocation();
  const currentPath = activeNavPath(location.pathname);
  const badgeCounts = useBadgeCounts();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === '1');

  const toggle = () => {
    setCollapsed((current) => {
      localStorage.setItem('sidebar-collapsed', current ? '0' : '1');
      return !current;
    });
  };

  const groupOwnsCurrent = (item) => !!item.children?.some((c) => c.path === currentPath);
  // Accordion: only one group is open at a time. Navigating into a group opens it (closing the rest).
  const [openGroup, setOpenGroup] = useState(() => navItems.find(groupOwnsCurrent)?.label ?? null);
  useEffect(() => {
    const owner = navItems.find(groupOwnsCurrent);
    if (owner) setOpenGroup(owner.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  const visibleItems = navItems
    .map((item) => {
      if (item.children) {
        const children = item.children.filter((c) => !c.perm || can(c.perm));
        return children.length > 0 ? { ...item, children } : null;
      }
      return !item.perm || can(item.perm) ? item : null;
    })
    .filter(Boolean);

  return (
    <aside
      className={`${
        collapsed ? 'w-[76px]' : 'w-64'
      } sticky top-0 h-screen shrink-0 flex flex-col bg-ink text-gray-300 shadow-panel transition-[width] duration-200 ease-out`}
    >
      <div className={`flex items-center gap-3 py-5 ${collapsed ? 'flex-col px-0' : 'px-5'}`}>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-dark text-sm font-bold text-white shadow-[0_4px_14px_-2px_rgba(220,38,38,0.55)]">
          D
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">Driver CRM</p>
            <p className="truncate text-[11px] text-gray-500">Recruitment Admin</p>
          </div>
        )}
        <button
          type="button"
          onClick={toggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-white/[0.04] hover:text-gray-300"
        >
          <IconPanelToggle className="h-[18px] w-[18px]" />
        </button>
      </div>

      <div className="space-y-1 border-b border-white/[0.06] p-3">
        <button
          type="button"
          onClick={() => window.location.reload()}
          title={collapsed ? 'Refresh' : undefined}
          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium text-gray-500 transition-colors hover:bg-white/[0.04] hover:text-gray-300 ${
            collapsed ? 'justify-center px-0' : ''
          }`}
        >
          <IconRefresh className="h-[18px] w-[18px] shrink-0" />
          {!collapsed && <span>Refresh</span>}
        </button>
      </div>

      <nav className="sidebar-scroll flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {visibleItems.map((item) =>
          item.children ? (
            <NavGroup
              key={item.label}
              group={item}
              collapsed={collapsed}
              badgeCounts={badgeCounts}
              isChildActive={groupOwnsCurrent(item)}
              open={openGroup === item.label}
              onToggle={() => setOpenGroup((current) => (current === item.label ? null : item.label))}
            />
          ) : (
            <NavRow key={item.path} path={item.path} label={item.label} badgeCount={badgeCounts[item.badge]} collapsed={collapsed} />
          )
        )}
      </nav>
    </aside>
  );
}
